import { useState, useEffect, useMemo } from 'react';
import '../styles/MenuCobro.css';

const METODOS = [
  { id: 'efectivo',      label: 'Efectivo',     icon: '💵' },
  { id: 'transferencia', label: 'Transf.',       icon: '🏦' },
  { id: 'debito',        label: 'Débito',        icon: '💳' },
  { id: 'credito',       label: 'Crédito',       icon: '🏧' },
  { id: 'point',         label: 'Point',         icon: '📟' },
];


const MenuCobro = ({ isOpen, onClose }) => {
  const [productos, setProductos]           = useState([]);
  const [busqueda, setBusqueda]             = useState('');
  const [itemsVenta, setItemsVenta]         = useState([]);
  const [metodoPago, setMetodoPago]         = useState('efectivo');
  const [montoRecibido, setMontoRecibido]   = useState('');
  const [clienteNombre, setClienteNombre]   = useState('');
  const [loading, setLoading]               = useState(false);
  const [loadingProds, setLoadingProds]     = useState(false);
  const [confirmacion, setConfirmacion]     = useState(null);
  const [error, setError]                   = useState('');

  // Point Smart (modo STANDALONE)
  const [dispositivos, setDispositivos]     = useState([]);
  const [dispositivoId, setDispositivoId]   = useState('');
  const [loadingDisp, setLoadingDisp]       = useState(false);
  const [errorDisp, setErrorDisp]           = useState('');
  const [cambiandoModo, setCambiandoModo]   = useState('');
  const [modoMsg, setModoMsg]               = useState('');

  // ─── Cargar productos al abrir ────────────────────────────────────────────
  useEffect(() => {
    if (!isOpen) return;
    setLoadingProds(true);
    fetch('/api/productos')
      .then(r => r.json())
      .then(data => setProductos(Array.isArray(data) ? data : []))
      .catch(() => setProductos([]))
      .finally(() => setLoadingProds(false));
  }, [isOpen]);

  // ─── Cargar dispositivos Point cuando se selecciona ese método ────────────
  const cargarDispositivos = () => {
    setLoadingDisp(true);
    setErrorDisp('');
    fetch('/api/point/dispositivos')
      .then(r => r.json())
      .then(data => {
        if (data.error) { setErrorDisp(data.error); return; }
        const lista = data.dispositivos || [];
        setDispositivos(lista);
        if (lista.length === 1) setDispositivoId(lista[0].id);
        if (lista.length === 0) setErrorDisp('No se encontraron lectores. Verificá que el dispositivo esté encendido y vinculado a tu cuenta de MercadoPago.');
      })
      .catch(err => setErrorDisp(`Error de conexión: ${err.message}`))
      .finally(() => setLoadingDisp(false));
  };

  // No auto-carga dispositivos — se cargan solo si el usuario abre la config

  // ─── Reset al cerrar ──────────────────────────────────────────────────────
  useEffect(() => {
    if (!isOpen) {
      setBusqueda('');
      setItemsVenta([]);
      setMetodoPago('efectivo');
      setMontoRecibido('');
      setClienteNombre('');
      setConfirmacion(null);
      setError('');
      setDispositivos([]);
      setDispositivoId('');
      setErrorDisp('');
      setCambiandoModo('');
      setModoMsg('');
    }
  }, [isOpen]);

  // ─── Cálculos ─────────────────────────────────────────────────────────────
  const productosFiltrados = useMemo(() => {
    const q = busqueda.trim().toLowerCase();
    if (!q) return productos;
    return productos.filter(p =>
      p.nombre.toLowerCase().includes(q) ||
      (p.categoria || '').toLowerCase().includes(q)
    );
  }, [productos, busqueda]);

  const total = useMemo(
    () => itemsVenta.reduce((acc, i) => acc + i.producto.precio * i.cantidad, 0),
    [itemsVenta]
  );

  const vuelto = useMemo(() => {
    if (metodoPago !== 'efectivo') return 0;
    const recibido = parseFloat(montoRecibido) || 0;
    return Math.max(0, recibido - total);
  }, [metodoPago, montoRecibido, total]);

  // ─── Manipulación de items ────────────────────────────────────────────────
  const agregarProducto = (producto) => {
    setItemsVenta(prev => {
      const existe = prev.find(i => i.producto._id === producto._id);
      if (existe) {
        return prev.map(i =>
          i.producto._id === producto._id ? { ...i, cantidad: i.cantidad + 1 } : i
        );
      }
      return [...prev, { producto, cantidad: 1 }];
    });
    setBusqueda('');
    setError('');
  };

  const cambiarCantidad = (id, delta) => {
    setItemsVenta(prev => {
      const item = prev.find(i => i.producto._id === id);
      if (!item) return prev;
      const nueva = item.cantidad + delta;
      if (nueva <= 0) return prev.filter(i => i.producto._id !== id);
      return prev.map(i => i.producto._id === id ? { ...i, cantidad: nueva } : i);
    });
  };

  const quitarItem = (id) => setItemsVenta(prev => prev.filter(i => i.producto._id !== id));

  // ─── Cambiar modo del dispositivo (PDV ↔ STANDALONE) ────────────────────
  const cambiarModoDispositivo = async (deviceId, modo) => {
    setCambiandoModo(deviceId);
    setModoMsg('');
    try {
      const res = await fetch(`/api/point/dispositivo/${deviceId}/modo`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ operating_mode: modo }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Error al cambiar modo');
      setModoMsg(`✓ Dispositivo cambiado a modo ${modo}`);
      // Recargar dispositivos para ver el modo actualizado
      cargarDispositivos();
    } catch (err) {
      setModoMsg(`⚠ ${err.message}`);
    } finally {
      setCambiandoModo('');
    }
  };

  // ─── Registrar cobro genérico ─────────────────────────────────────────────
  const _guardarCobro = async (metodo) => {
    try {
      const res = await fetch('/api/cobros', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          productos: itemsVenta.map(i => ({
            _id:      i.producto._id,
            nombre:   i.producto.nombre,
            precio:   i.producto.precio,
            cantidad: i.cantidad,
          })),
          total,
          metodoPago: metodo,
          montoRecibido: metodo === 'efectivo' ? (parseFloat(montoRecibido) || total) : total,
          vuelto:        metodo === 'efectivo' ? vuelto : 0,
          clienteNombre: clienteNombre.trim() || 'Cliente mostrador',
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Error al registrar la venta');
      setConfirmacion(data);
    } catch (err) {
      setError(err.message);
    }
  };

  // ─── Registrar venta (métodos no-Point) ──────────────────────────────────
  const registrarVenta = async () => {
    if (itemsVenta.length === 0) { setError('Agregá al menos un producto.'); return; }
    if (metodoPago === 'efectivo') {
      const recibido = parseFloat(montoRecibido) || 0;
      if (recibido < total) { setError('El monto recibido es menor al total.'); return; }
    }
    setLoading(true);
    setError('');
    await _guardarCobro(metodoPago);
    setLoading(false);
  };

  const nuevaVenta = () => {
    setItemsVenta([]);
    setMetodoPago('efectivo');
    setMontoRecibido('');
    setClienteNombre('');
    setConfirmacion(null);
    setError('');
  };

  if (!isOpen) return null;

  const metodoPagoLabel = METODOS.find(m => m.id === (confirmacion?.metodoPago || metodoPago));
  const pointActivo = metodoPago === 'point';

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <div className="cobro-overlay" onClick={onClose}>
      <div className="cobro-panel" onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="cobro-header">
          <div>
            <h2 className="cobro-titulo">Cobro</h2>
            <p className="cobro-subtitulo">Venta en mostrador</p>
          </div>
          <button className="cobro-close" onClick={onClose} aria-label="Cerrar">✕</button>
        </div>

        {/* ── PANTALLA DE CONFIRMACIÓN ── */}
        {confirmacion ? (
          <div className="cobro-confirmacion">
            <div className="cobro-check-circle">✓</div>
            <h3 className="cobro-conf-titulo">¡Venta registrada!</h3>
            <p className="cobro-conf-id">{confirmacion.id_pedido}</p>
            <p className="cobro-conf-hora">{confirmacion.fecha} · {confirmacion.hora}</p>

            <div className="cobro-conf-items">
              {confirmacion.productos?.map((p, i) => (
                <div key={i} className="cobro-conf-row">
                  <span>{p.nombre} <em>× {p.cantidad}</em></span>
                  <span>${(p.precio * p.cantidad).toLocaleString('es-AR')}</span>
                </div>
              ))}
              <div className="cobro-conf-total-row">
                <span>Total</span>
                <span>${confirmacion.total?.toLocaleString('es-AR')}</span>
              </div>
              {confirmacion.vuelto > 0 && (
                <div className="cobro-conf-vuelto-row">
                  <span>Vuelto</span>
                  <span>${confirmacion.vuelto?.toLocaleString('es-AR')}</span>
                </div>
              )}
            </div>

            {metodoPagoLabel && (
              <div className="cobro-conf-metodo">
                {metodoPagoLabel.icon} {metodoPagoLabel.label}
              </div>
            )}

            <div className="cobro-conf-acciones">
              <button className="cobro-btn-nueva" onClick={nuevaVenta}>+ Nueva venta</button>
              <button className="cobro-btn-cerrar-conf" onClick={onClose}>Cerrar</button>
            </div>
          </div>

        ) : (
          /* ── FLUJO DE VENTA ── */
          <div className="cobro-body">

            {/* 1. Buscador */}
            <section className="cobro-seccion">
              <label className="cobro-label">Agregar producto</label>
              <div className="cobro-search-wrap">
                <input
                  className="cobro-input"
                  type="text"
                  placeholder="Buscar por nombre o categoría..."
                  value={busqueda}
                  onChange={e => setBusqueda(e.target.value)}
                  autoComplete="off"
                />
                {busqueda && (
                  <button className="cobro-search-clear" onClick={() => setBusqueda('')}>✕</button>
                )}
              </div>
              {busqueda && (
                <div className="cobro-sugerencias">
                  {loadingProds && (
                    <div className="cobro-sug-item cobro-sug-info">Cargando...</div>
                  )}
                  {!loadingProds && productosFiltrados.length === 0 && (
                    <div className="cobro-sug-item cobro-sug-info">Sin resultados</div>
                  )}
                  {productosFiltrados.slice(0, 8).map(p => (
                    <button
                      key={p._id}
                      className="cobro-sug-item"
                      onClick={() => agregarProducto(p)}
                    >
                      <span className="cobro-sug-nombre">{p.nombre}</span>
                      <span className="cobro-sug-precio">
                        ${p.precio?.toLocaleString('es-AR')}
                        {(p.stock ?? null) !== null && (
                          <span className="cobro-sug-stock"> · stock: {p.stock}</span>
                        )}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </section>

            {/* 2. Items de la venta */}
            <section className="cobro-seccion cobro-seccion-items">
              {itemsVenta.length === 0 ? (
                <p className="cobro-vacia">Buscá productos para agregarlos a la venta</p>
              ) : (
                <ul className="cobro-lista">
                  {itemsVenta.map(item => (
                    <li key={item.producto._id} className="cobro-item">
                      <div className="cobro-item-info">
                        <span className="cobro-item-nombre">{item.producto.nombre}</span>
                        <span className="cobro-item-subtotal">
                          ${(item.producto.precio * item.cantidad).toLocaleString('es-AR')}
                        </span>
                      </div>
                      <div className="cobro-item-ctrl">
                        <button onClick={() => cambiarCantidad(item.producto._id, -1)}>−</button>
                        <span className="cobro-item-qty">{item.cantidad}</span>
                        <button onClick={() => cambiarCantidad(item.producto._id, +1)}>+</button>
                        <button className="cobro-item-quitar" onClick={() => quitarItem(item.producto._id)} title="Quitar">✕</button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}

              {itemsVenta.length > 0 && (
                <div className="cobro-total-row">
                  <span>Total</span>
                  <span className="cobro-total-num">${total.toLocaleString('es-AR')}</span>
                </div>
              )}
            </section>

            {/* 3. Cliente (opcional) */}
            <section className="cobro-seccion">
              <label className="cobro-label">Cliente <span className="cobro-opcional">(opcional)</span></label>
              <input
                className="cobro-input"
                type="text"
                placeholder="Nombre del cliente"
                value={clienteNombre}
                onChange={e => setClienteNombre(e.target.value)}
              />
            </section>

            {/* 4. Método de pago */}
            <section className="cobro-seccion">
              <label className="cobro-label">Método de pago</label>
              <div className="cobro-metodos">
                {METODOS.map(m => (
                  <button
                    key={m.id}
                    className={`cobro-metodo-btn ${metodoPago === m.id ? 'activo' : ''}`}
                    onClick={() => {
                      setMetodoPago(m.id);
                      setMontoRecibido('');
                      setPointEstado('idle');
                      setPointMensaje('');
                      detenerPolling();
                    }}
                  >
                    <span className="cobro-metodo-icon">{m.icon}</span>
                    <span className="cobro-metodo-label">{m.label}</span>
                  </button>
                ))}
              </div>
            </section>

            {/* 5. Efectivo: monto recibido y vuelto */}
            {metodoPago === 'efectivo' && itemsVenta.length > 0 && (
              <section className="cobro-seccion cobro-seccion-efectivo">
                <label className="cobro-label">Monto recibido</label>
                <input
                  className="cobro-input cobro-input-monto"
                  type="number"
                  placeholder={`mín. $${total.toLocaleString('es-AR')}`}
                  value={montoRecibido}
                  onChange={e => setMontoRecibido(e.target.value)}
                  min="0"
                />
                {parseFloat(montoRecibido) >= total && total > 0 && (
                  <div className="cobro-vuelto">
                    <span>Vuelto</span>
                    <span className="cobro-vuelto-num">${vuelto.toLocaleString('es-AR')}</span>
                  </div>
                )}
              </section>
            )}

            {/* 6. Point Smart (modo STANDALONE) */}
            {pointActivo && (
              <section className="cobro-seccion cobro-seccion-point">

                {/* Monto a cobrar en el posnet */}
                {itemsVenta.length > 0 && (
                  <div className="cobro-point-instruccion">
                    <p className="cobro-point-instruccion-txt">Cobrá este monto en el posnet:</p>
                    <p className="cobro-point-monto-grande">${total.toLocaleString('es-AR')}</p>
                    <p className="cobro-point-instruccion-sub">Ingresá el importe manualmente en el dispositivo y procesá el pago. Cuando esté aprobado, presioná "Confirmar cobro" abajo.</p>
                  </div>
                )}

                {/* Gestión de dispositivos (para cambiar modo si hace falta) */}
                <details className="cobro-point-config" onToggle={e => { if (e.target.open && dispositivos.length === 0 && !loadingDisp) cargarDispositivos(); }}>
                  <summary className="cobro-point-config-toggle">⚙ Configurar dispositivo (opcional)</summary>
                  <div className="cobro-point-config-body">
                    {loadingDisp ? (
                      <p className="cobro-point-info">Buscando lectores...</p>
                    ) : errorDisp ? (
                      <div className="cobro-point-warn-box">
                        <p className="cobro-point-warn-msg">{errorDisp}</p>
                        <button className="cobro-point-btn-retry" onClick={cargarDispositivos}>↺ Reintentar</button>
                      </div>
                    ) : dispositivos.length === 0 ? (
                      <div className="cobro-point-warn-box">
                        <p className="cobro-point-warn-msg">No se encontraron lectores.</p>
                        <button className="cobro-point-btn-retry" onClick={cargarDispositivos}>↺ Buscar</button>
                      </div>
                    ) : (
                      <ul className="cobro-point-device-list">
                        {dispositivos.map(d => (
                          <li key={d.id} className="cobro-point-device-item">
                            <span className="cobro-point-device-id">
                              📟 {d.external_pos_id || d.id}
                              {d.operating_mode && (
                                <span className={`cobro-point-device-modo ${d.operating_mode === 'STANDALONE' ? 'standalone' : 'pdv'}`}>
                                  {d.operating_mode}
                                </span>
                              )}
                            </span>
                            {d.operating_mode !== 'STANDALONE' && (
                              <button
                                className="cobro-point-btn-modo"
                                disabled={cambiandoModo === d.id}
                                onClick={() => cambiarModoDispositivo(d.id, 'STANDALONE')}
                              >
                                {cambiandoModo === d.id ? '...' : 'Cambiar a STANDALONE'}
                              </button>
                            )}
                            {d.operating_mode === 'STANDALONE' && (
                              <button
                                className="cobro-point-btn-modo cobro-point-btn-modo-pdv"
                                disabled={cambiandoModo === d.id}
                                onClick={() => cambiarModoDispositivo(d.id, 'PDV')}
                              >
                                {cambiandoModo === d.id ? '...' : 'Cambiar a PDV'}
                              </button>
                            )}
                          </li>
                        ))}
                      </ul>
                    )}
                    {modoMsg && <p className="cobro-point-modo-msg">{modoMsg}</p>}
                  </div>
                </details>

              </section>
            )}

            {/* Error */}
            {error && <div className="cobro-error">⚠️ {error}</div>}

            {/* Botón acción */}
            <div className="cobro-footer">
              <button
                className={`cobro-btn-registrar${pointActivo ? ' cobro-btn-point' : ''}`}
                onClick={registrarVenta}
                disabled={loading || itemsVenta.length === 0}
              >
                {loading
                  ? 'Registrando...'
                  : pointActivo && itemsVenta.length > 0
                    ? `📟 Confirmar cobro · $${total.toLocaleString('es-AR')}`
                    : itemsVenta.length > 0
                      ? `Registrar venta · $${total.toLocaleString('es-AR')}`
                      : 'Registrar venta'
                }
              </button>
            </div>

          </div>
        )}
      </div>
    </div>
  );
};

export default MenuCobro;
